import { supabase } from '../../shared/supabase/supabaseClient';
import { createKeyedTtlCache } from '../../shared/data/keyedTtlCache';
import { registerSessionCacheClearHandler } from '../../shared/ports/sessionResetPort';
import type { ScheduledClosure } from '../domain/storeProfileTypes';

type RemoteClosureRow = {
  id: string;
  start_date: string;
  end_date: string;
  reason: string | null;
};

function closureRpcError(error: { message?: string } | null): Error {
  const raw = (error?.message ?? '').toLowerCase();
  if (raw.includes('end_must_be_after_start') || raw.includes('dates_required')) {
    return new Error('Revisá las fechas: “hasta” tiene que ser después de “desde”.');
  }
  if (
    raw.includes('failed to fetch') ||
    raw.includes('network') ||
    raw.includes('offline') ||
    raw.includes('fetch')
  ) {
    return new Error('Necesitás conexión para gestionar cierres programados.');
  }
  return new Error(error?.message || 'No se pudo guardar el cierre programado.');
}

// Las sub-pantallas de Ajustes comparten useBranchSettingsScreen: el listado se
// pide en cada mount. Cambia solo vía apply_scheduled_closure_command (abajo).
const closuresCache = createKeyedTtlCache(60_000);

export async function listScheduledClosures(
  branchId: string,
  opts?: { force?: boolean }
): Promise<ScheduledClosure[]> {
  const id = branchId.trim();
  if (!id) return [];
  return closuresCache.get(
    id,
    async () => {
      const { data, error } = await supabase
        .from('scheduled_closures')
        .select('id, start_date, end_date, reason')
        .eq('branch_id', id)
        .gte('end_date', new Date().toISOString())
        .order('start_date', { ascending: true });
      if (error || !data) return [];
      return (data as RemoteClosureRow[]).map((c) => ({
        id: c.id,
        startDate: c.start_date,
        endDate: c.end_date,
        reason: c.reason,
      }));
    },
    { force: opts?.force }
  );
}

/** Escritura online (sin cola de sync). */
export async function createScheduledClosure(params: {
  branchId: string;
  startDate: string;
  endDate: string;
  reason?: string;
}): Promise<void> {
  const { branchId, startDate, endDate, reason } = params;
  const operationId = crypto.randomUUID();
  const { error } = await supabase.rpc('apply_scheduled_closure_command', {
    p_operation: 'insert',
    p_payload: {
      branchId,
      operationId,
      startDate,
      endDate,
      reason: reason?.trim() || null,
    },
  });
  if (error) throw closureRpcError(error);
  closuresCache.invalidate(branchId);
}

export async function deleteScheduledClosure(id: string, branchId: string): Promise<void> {
  const operationId = crypto.randomUUID();
  const { error } = await supabase.rpc('apply_scheduled_closure_command', {
    p_operation: 'delete',
    p_payload: {
      id,
      branchId,
      operationId,
    },
  });
  if (error) throw closureRpcError(error);
  closuresCache.invalidate(branchId);
}

registerSessionCacheClearHandler(() => closuresCache.clear());
